/**
 * Asset Currency Utility
 * Determines the currency of an asset based on its type and exchange
 */

import { Asset } from '@/types/api';

/**
 * Get the currency code for an asset
 * @param asset The asset to determine currency for
 * @returns Currency code (e.g., 'USD', 'HKD', 'CNY')
 */
export function getAssetCurrency(asset: Asset): string {
  // For forex assets, extract the target currency from symbol
  if (asset.type === 'forex') {
    if (asset.symbol.includes('/')) {
      const parts = asset.symbol.split('/');
      if (parts.length === 2 && parts[1]) {
        return parts[1].trim().toUpperCase();
      }
    } else if (asset.symbol.includes('_')) {
      const parts = asset.symbol.split('_');
      if (parts.length === 2 && parts[1]) {
        return parts[1].trim().toUpperCase();
      }
    }
    // Default to USD for forex if we can't parse
    return 'USD';
  }

  // For stocks, determine currency based on exchange
  if (asset.type === 'stock') {
    const exchange = asset.exchange?.toUpperCase();
    const symbolUpper = asset.symbol.toUpperCase();

    // Hong Kong stocks
    if (exchange === 'HK' || symbolUpper.endsWith('.HK')) {
      return 'HKD';
    }

    // Chinese stocks (Shanghai, Shenzhen, Beijing)
    if (
      exchange === 'SH' ||
      exchange === 'SZ' ||
      exchange === 'BJ' ||
      symbolUpper.endsWith('.SH') ||
      symbolUpper.endsWith('.SZ') ||
      symbolUpper.endsWith('.BJ')
    ) {
      return 'CNY';
    }

    // Default to USD for other stocks (US, etc.)
    return 'USD';
  }

  // For crypto, index, and bond, default to USD
  return 'USD';
}

/**
 * Check if an asset's currency matches the base currency
 * @param asset The asset to check
 * @param baseCurrency The base currency to compare against
 * @returns True if the asset's currency matches the base currency
 */
export function isAssetCurrencyMatch(asset: Asset, baseCurrency: string): boolean {
  return getAssetCurrency(asset).toUpperCase() === baseCurrency.toUpperCase();
}
